import React, { useEffect } from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  Edit,
  EditButton,
  Filter,
  NullableBooleanInput,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SearchInput,
  TextField,
  UrlField,
  useTranslate,
} from 'react-admin';
import withStyles from '@material-ui/core/styles/withStyles';
import TabForm from './TabForm';
import ChipsField from './ChipsField';
import ImageLinkField from '../ImageLinkField';
import PriceField from '../coupons/PriceField';
import TextFieldWithPrependAppend from '../TextFieldWithPrependAppend';
import { ZippityDatagrid } from '../../components';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import CustomTitle from '../../components/CustomTitle';
import ReferenceChild from '../../components/ReferenceChild';
import {
  formatScheduleTypeName,
  getZ3PConfigValue,
  perPage,
} from '../../helper';
import CatalogServiceForm from './catalog';
import ListWithCSVImport from '../../components/ListWithCSVImport';
import ScheduleTypeChipsField from '../schedule_type/ScheduleTypeChipsField';
import { apiGet } from '../../brainApi';

const ServiceOfferingFilter = (props) => {
  const translate = useTranslate();
  const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
  return (
    <Filter {...props}>
      <SearchInput source="q" alwaysOn />
      <NullableBooleanInput source="bookable" defaultValue />
      <NullableBooleanInput source="upsellable" defaultValue />
      <NullableBooleanInput source="add_oil_cost" defaultValue />
      <NullableBooleanInput source="has_custom_message" defaultValue />
      <NullableBooleanInput
        source="included_with_every_mechanical_service"
        defaultValue
      />
      {!isSkippingVehicle && (
        <ReferenceInput
          source="schedule_type_id"
          reference="schedule-type"
          sort={{ field: 'name', order: 'ASC' }}
          perPage={perPage}
        >
          <ReferenceChild
            {...props}
            optionText={(option) =>
              formatScheduleTypeName(option.name, translate)
            }
            source="schedule_type_id"
          />
        </ReferenceInput>
      )}
      <BooleanInput source="is_parent" defaultValue />
    </Filter>
  );
};

const listStyles = {
  nb_commands: { color: 'purple' },
  tax_type: { fontSize: '10px' },
  center: { textAlign: 'center' },
};

export const ServiceOfferingList = withStyles(listStyles)(
  ({ classes, ...props }) => {
  
    useEffect(() => {
      getHighestDisplayOrder();
    }, []);
    
    const getHighestDisplayOrder = () => {
      apiGet('/service-offering').then((services) => {
        if (services.length > 0) {
          for (let i = 0; i < services.length; i++) {
            if (services[i].short_name === 'New Tire Installation') {
              localStorage.setItem('ServiceId', services[i].service_offering_id);
              break;
            }
          }
        }
      });
    };

    const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');
    const vehicleRelatedFields = [
      <ChipsField source="offered_vehicle_makes" sortable={false} />,
      <BooleanField source="add_oil_cost" />,
      <BooleanField source="per_tire" />,
      <BooleanField source="is_atd_tire_product" />,
      <BooleanField source="is_tire_installation_fee" />,
      <ScheduleTypeChipsField source="schedule_types" sortable={false} />,
    ];

    return (
      <div>
        <ListWithCSVImport
          {...props}
          filters={<ServiceOfferingFilter />}
          bulkActionButtons={<BulkDeleteButton />}
          sort={{ field: 'service_offering_id', order: 'ASC' }}
          perPage={50}
        >
          <ZippityDatagrid>
            <TextField source="created_at" />
            <TextField source="modified_at" />
            <TextField source="service_offering_id" />
            <TextField source="display_order" />
            <TextField source="long_name" />
            <TextField source="short_name" />
            <TextField source="tiny_internal_name" />
            <BooleanField source="bookable" />
            <BooleanField source="upsellable" />
            <BooleanField source="variable_pricing" />
            <TextField source="display_superscript" />
            <BooleanField source="display_emphasize" />
            <UrlField source="marketing_url" />
            <ChipsField source="tax_type" />
            <ChipsField source="maintenance_repair_category" />
            <TextFieldWithPrependAppend
              source="historical_parts_cost_percentage"
              append="%"
            />
            <ChipsField source="display_category" />
            <NumberField source="labor_hours" />
            <ChipsField
              source="custom_message_1"
              subSource="internal_name"
              sortable={false}
            />
            <ChipsField
              source="custom_message_2"
              subSource="internal_name"
              sortable={false}
            />
            <BooleanField source="included_with_every_mechanical_service" />
            <ImageLinkField source="inline_image_url" sortable={false} />
            <ImageLinkField source="card_image_url" sortable={false} />
            <PriceField source="original_value_dollars" />
            <TextField source="subtitle" />
            <BooleanField source="is_parent" />
            <ChipsField source="child_service_offerings" sortable={false} />
            <ReferenceField
              source="parent_service_offering_id"
              reference="service-offering"
              link="edit"
              allowEmpty
            >
              <TextField source="short_name" />
            </ReferenceField>
            {!isSkippingVehicle ? vehicleRelatedFields : <></>}
            <EditButton />
          </ZippityDatagrid>
        </ListWithCSVImport>
      </div>
    );
  },
);

const editStyles = {
  service_offering_id: { display: 'inline-block' },
  referenceArrayWide: { width: '100%' },
};

export const ServiceOfferingEdit = withStyles(editStyles)(
  ({ classes, ...props }) => (
    <Edit
      undoable={false}
      title={<CustomTitle title="resources.service-offering.title" />}
      {...props}
    >
      <TabForm
        record={props.record}
        resource={props.resource}
        classes={classes}
        create={false}
        {...props}
      />
    </Edit>
  ),
);

export const ServiceOfferingCreate = withStyles(editStyles)(
  ({ classes, ...props }) => (
    <Create {...props}>
      <TabForm classes={classes} {...props} create />
    </Create>
  ),
);

const catalogEditStyles = {
  card: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
};

export const CatalogCreate = withStyles(catalogEditStyles)(
  ({ classes, ...props }) => (
    <Create {...props} classes={{ card: classes.card }}>
      <CatalogServiceForm {...props} />
    </Create>
  ),
);

export const CatalogEdit = withStyles(catalogEditStyles)(
  ({ classes, ...props }) => (
    <Edit
      {...props}
      classes={{ card: classes.card }}
      title={<CustomTitle title="resources.service-offering.title" />}
    >
      <CatalogServiceForm {...props} resource="service-offering" />
    </Edit>
  ),
);
